import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TECHNICIAN_ERROR_MESSAGES } from '../errors';
import {
  validateName,
  validatePhone,
  validateCountryCode,
  validateAddress,
  validateCity,
  validateState,
  validateZip,
} from '../utils/validation';
import { getTechnicianByIdApiService } from '../api/getTechnicianByIdApiService';
import { getTechnicianPrivilegesApiService } from '../api/getTechnicianPrivilegesApiService';
import { editTechnicianApiService } from '../api/editTechnicianApiService';
import { deleteMediaResource } from '../api/deleteMediaResourceApiService';
import { updateTechnicianStatusApiService } from '../api/updateTechnicianStatusApiService';
import { getPresignedUrlsBatch } from '../api/presignedUrlBatchApiService';
import { uploadFileToS3 } from '../api/uploadFileToS3';
import {
  TechnicianDetailResponse,
  UpdateTechnicianRequest,
} from '../types/editTechnicianType';
import {
  MediaResource,
  TechnicianPrivileges,
} from '../types/addTechnicianType';

const initialPrivileges: TechnicianPrivileges = {
  taskView: false,
  taskUpdateStatus: false,
  taskFillForm: false,
  taskAcceptReject: false,
  submittedTaskFormList: true,
  submittedTaskFormView: true,
  certificateList: false,
  certificateView: false,
  certificateDownload: false,
  invoiceList: false,
  invoiceView: false,
  invoiceDownload: false,
};

interface EditTechnicianFormData {
  name: string;
  phone: string;
  countryCode: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  email: string;
  role: string;
  technician_id: string;
  privileges: TechnicianPrivileges;
}

export interface UseEditTechnicianReturn {
  formData: EditTechnicianFormData;
  setFormData: React.Dispatch<React.SetStateAction<EditTechnicianFormData>>;
  errors: Record<string, string>;
  submitErrorTrigger: number;
  isSubmitting: boolean;
  isLoading: boolean;
  accessError: boolean;
  snackbarError: string;
  isSnackbarOpen: boolean;
  handleCloseSnackbar: () => void;
  isCancelModalOpen: boolean;
  handleCloseCancelModal: () => void;
  handleConfirmCancel: () => void;
  handleNameChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handlePhoneChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleCountryCodeChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleAddress1Change: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleAddress2Change: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleCityChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleStateChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleZipChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleProfileFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleProfileFileDelete: () => void;
  handleProfileFileError: (message: string) => void;
  handleDocFilesChange: (files: File[]) => void;
  handleExistingDocDelete: (path: string) => void;
  handleDocFilesError: (message: string) => void;
  handlePrivilegesChange: (privileges: TechnicianPrivileges) => void;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  handleCancel: () => void;
  profileFile: File | undefined;
  existingProfileUrl: string | undefined;
  docFiles: File[];
  existingDocs: Array<{ filename: string; fileSize: number; path: string }>;
  status: 'active' | 'inactive';
  handleStatusChange: () => void;
  isStatusDialogOpen: boolean;
  handleCloseStatusDialog: () => void;
  handleConfirmStatusUpdate: () => Promise<void>;
  isStatusSubmitting: boolean;
  showStatusSuccessSnackbar: boolean;
  statusSuccessMessage: string;
  handleCloseStatusSuccessSnackbar: () => void;
  showStatusErrorSnackbar: boolean;
  statusErrorMessage: string;
  handleCloseStatusErrorSnackbar: () => void;
}

const initialFormData: EditTechnicianFormData = {
  name: '',
  phone: '',
  countryCode: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  country: '',
  email: '',
  role: '',
  technician_id: '',
  privileges: initialPrivileges,
};

export const useEditTechnician = (): UseEditTechnicianReturn => {
  const navigate = useNavigate();
  const { technicianId } = useParams<{ technicianId: string }>();

  const [formData, setFormData] =
    useState<EditTechnicianFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [accessError, setAccessError] = useState(false);
  const [snackbarError, setSnackbarError] = useState('');
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [profileFile, setProfileFile] = useState<File | undefined>();
  const [existingProfileUrl, setExistingProfileUrl] = useState<
    string | undefined
  >();
  const [existingProfilePath, setExistingProfilePath] = useState<
    string | undefined
  >();
  const [docFiles, setDocFiles] = useState<File[]>([]);
  const [existingDocs, setExistingDocs] = useState<
    Array<{ filename: string; fileSize: number; path: string }>
  >([]);
  const [deletedDocPaths, setDeletedDocPaths] = useState<string[]>([]);
  const [status, setStatus] = useState<'active' | 'inactive'>('active');
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [isStatusSubmitting, setIsStatusSubmitting] = useState(false);
  const [showStatusSuccessSnackbar, setShowStatusSuccessSnackbar] =
    useState(false);
  const [statusSuccessMessage, setStatusSuccessMessage] = useState('');
  const [showStatusErrorSnackbar, setShowStatusErrorSnackbar] = useState(false);
  const [statusErrorMessage, setStatusErrorMessage] = useState('');
  const [submitErrorTrigger, setSubmitErrorTrigger] = useState(0);

  // Snapshot of the technician at load time, used to detect "no edits" on
  // cancel. Status is intentionally excluded — the status toggle has its own
  // confirmation dialog and API call, so it's independent of the form's
  // save/cancel lifecycle.
  const originalRef = useRef<{
    formData: EditTechnicianFormData;
    profilePath: string | undefined;
    docPaths: string[];
  } | null>(null);

  const PHONE_MAX_LENGTH = 9;

  const getExistingDocs = (
    docs?: TechnicianDetailResponse['docs']
  ): Array<{ filename: string; fileSize: number; path: string }> => {
    if (!docs || !Array.isArray(docs) || docs.length === 0) {
      return [];
    }
    return docs
      .filter((d) => d.isUploaded)
      .map((doc) => ({
        filename: doc.filename,
        fileSize: doc.fileSize,
        path: doc.path,
      }));
  };

  useEffect(() => {
    const loadTechnician = async () => {
      if (!technicianId) {
        return;
      }

      try {
        const [response, fetchedPrivileges] = await Promise.all([
          getTechnicianByIdApiService(technicianId),
          getTechnicianPrivilegesApiService(technicianId),
        ]);

        if (response.forbidden) {
          setAccessError(true);
          return;
        }

        if (response.success && response.data) {
          const data = response.data;
          const loadedPrivileges: TechnicianPrivileges =
            fetchedPrivileges ?? initialPrivileges;
          setFormData({
            name: data.name || '',
            phone: data.phone_number || data.phoneNumber || '',
            countryCode: data.country_code || data.countryCode || '',
            address1: data.address_line1 || data.addressLine1 || '',
            address2: data.address_line2 || data.addressLine2 || '',
            city: data.city || '',
            state: data.state || '',
            zip: data.zip_code || data.zipCode || '',
            country: data.country || '',
            email: data.email || '',
            role: data.role || '',
            technician_id: data.technician_id || '',
            privileges: loadedPrivileges,
          });

          setStatus(data.status === 'active' ? 'active' : 'inactive');

          if (data.profile?.isUploaded && data.profile.path) {
            setExistingProfilePath(data.profile.path);
            if (data.profile.url) {
              setExistingProfileUrl(data.profile.url);
            }
          }

          const docs = getExistingDocs(data.docs);
          setExistingDocs(docs);

          originalRef.current = {
            formData: {
              name: data.name || '',
              phone: data.phone_number || data.phoneNumber || '',
              countryCode: data.country_code || data.countryCode || '',
              address1: data.address_line1 || data.addressLine1 || '',
              address2: data.address_line2 || data.addressLine2 || '',
              city: data.city || '',
              state: data.state || '',
              zip: data.zip_code || data.zipCode || '',
              country: data.country || '',
              email: data.email || '',
              role: data.role || '',
              technician_id: data.technician_id || '',
              privileges: loadedPrivileges,
            },
            profilePath:
              data.profile?.isUploaded && data.profile.path
                ? data.profile.path
                : undefined,
            docPaths: docs.map((d) => d.path),
          };
        } else if (!response.success) {
          setSnackbarError(TECHNICIAN_ERROR_MESSAGES.UNEXPECTED_ERROR);
          setIsSnackbarOpen(true);
        }
      } catch (_error) {
        setSnackbarError(TECHNICIAN_ERROR_MESSAGES.UNEXPECTED_ERROR);
        setIsSnackbarOpen(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadTechnician();
  }, [technicianId]);

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      setFormData((prev) => ({ ...prev, name: value }));
      setErrors((prev) => ({ ...prev, name: validateName(value) }));
    },
    []
  );

  const handlePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      let value = e.target.value.replace(/\D/g, '');
      if (value.length > PHONE_MAX_LENGTH) {
        value = value.slice(0, PHONE_MAX_LENGTH);
      }
      setFormData((prev) => ({ ...prev, phone: value }));
      setErrors((prev) => ({ ...prev, phone: '' }));
    },
    []
  );

  const handleCountryCodeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      setFormData((prev) => ({ ...prev, countryCode: value }));
      setErrors((prev) => ({
        ...prev,
        countryCode: validateCountryCode(value),
      }));
    },
    []
  );

  const handleAddress1Change = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      setFormData((prev) => ({ ...prev, address1: value }));
      setErrors((prev) => ({ ...prev, address1: validateAddress(value) }));
    },
    []
  );

  const handleAddress2Change = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      setFormData((prev) => ({ ...prev, address2: value }));
    },
    []
  );

  const handleCityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      setFormData((prev) => ({ ...prev, city: value }));
      setErrors((prev) => ({ ...prev, city: validateCity(value) }));
    },
    []
  );

  const handleStateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      setFormData((prev) => ({ ...prev, state: value }));
      setErrors((prev) => ({ ...prev, state: validateState(value) }));
    },
    []
  );

  const handleZipChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value.trim();
      setFormData((prev) => ({ ...prev, zip: value }));
      setErrors((prev) => ({ ...prev, zip: validateZip(value) }));
    },
    []
  );

  const handleProfileFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        setProfileFile(file);
        if (existingProfilePath) {
          setDeletedDocPaths((prev) => [...prev, existingProfilePath]);
        }
        setExistingProfileUrl(undefined);
        setExistingProfilePath(undefined);
      }
    },
    [existingProfilePath]
  );

  const handleProfileFileDelete = useCallback(() => {
    setProfileFile(undefined);
    if (existingProfilePath) {
      setDeletedDocPaths((prev) => [...prev, existingProfilePath]);
    }
    setExistingProfileUrl(undefined);
    setExistingProfilePath(undefined);
  }, [existingProfilePath]);

  const handleProfileFileError = useCallback((message: string) => {
    setSnackbarError(message);
    setIsSnackbarOpen(true);
  }, []);

  const handleDocFilesChange = useCallback((files: File[]) => {
    setDocFiles(files);
  }, []);

  const handleExistingDocDelete = useCallback((path: string) => {
    // Remove from existing docs display
    setExistingDocs((prev) => prev.filter((doc) => doc.path !== path));
    // Add to deletion queue
    setDeletedDocPaths((prev) => [...prev, path]);
  }, []);

  const handleDocFilesError = useCallback((message: string) => {
    setSnackbarError(message);
    setIsSnackbarOpen(true);
  }, []);

  const handlePrivilegesChange = useCallback(
    (privileges: TechnicianPrivileges) => {
      setFormData((prev) => ({ ...prev, privileges }));
    },
    []
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const validationErrors: Record<string, string> = {
        name: validateName(formData.name),
        phone: validatePhone(formData.phone),
        countryCode: validateCountryCode(formData.countryCode),
        address1: validateAddress(formData.address1),
        city: validateCity(formData.city),
        state: validateState(formData.state),
        zip: validateZip(formData.zip),
      };

      const hasErrors = Object.values(validationErrors).some(
        (error) => error !== ''
      );
      if (hasErrors) {
        setErrors(validationErrors);
        setSubmitErrorTrigger((prev) => prev + 1);
        return;
      }

      if (!technicianId) {
        return;
      }

      setIsSubmitting(true);
      try {
        // Step 1: Upload new files first
        let newProfile: MediaResource | undefined;
        let newDocs: MediaResource[] | undefined;

        if (profileFile || docFiles.length > 0) {
          const paths: string[] = [];

          if (profileFile) {
            paths.push(
              `technicians/${technicianId}/profiles/resourceid-placeholder/${profileFile.name}`
            );
          }

          if (docFiles.length > 0) {
            docFiles.forEach((file) => {
              paths.push(
                `technicians/${technicianId}/docs/resourceid-placeholder/${file.name}`
              );
            });
          }

          // Get presigned URLs (no entityId generated, just resourceIds)
          const presignedResponse = await getPresignedUrlsBatch({
            paths,
            generatePath: true,
          });

          // Upload files
          let fileIndex = 0;

          if (profileFile) {
            const fileData = presignedResponse.files[fileIndex];
            if (fileData) {
              await uploadFileToS3(fileData.url, profileFile);

              newProfile = {
                path: fileData.path,
                resourceId: fileData.resourceId,
                isUploaded: true,
                filename: profileFile.name,
                fileType: profileFile.type,
                fileSize: profileFile.size,
              };

              fileIndex++;
            }
          }

          if (docFiles.length > 0) {
            newDocs = [];
            for (const docFile of docFiles) {
              const fileData = presignedResponse.files[fileIndex];
              if (fileData) {
                await uploadFileToS3(fileData.url, docFile);

                newDocs.push({
                  path: fileData.path,
                  resourceId: fileData.resourceId,
                  isUploaded: true,
                  filename: docFile.name,
                  fileType: docFile.type,
                  fileSize: docFile.size,
                });

                fileIndex++;
              }
            }
          }
        }

        // Step 2: Delete old media after successful upload
        if (deletedDocPaths.length > 0) {
          await Promise.all(
            deletedDocPaths.map((path) => deleteMediaResource(path))
          );
        }

        // Step 3: Update technician with only changed fields
        const original = originalRef.current?.formData;
        const payload: UpdateTechnicianRequest = {};
        if (!original || formData.name !== original.name) {
          payload.name = formData.name;
        }
        if (!original || formData.phone !== original.phone) {
          payload.phoneNumber = formData.phone;
        }
        if (!original || formData.countryCode !== original.countryCode) {
          payload.countryCode = formData.countryCode;
        }
        if (!original || formData.address1 !== original.address1) {
          payload.addressLine1 = formData.address1;
        }
        if (!original || formData.address2 !== original.address2) {
          payload.addressLine2 = formData.address2;
        }
        if (!original || formData.city !== original.city) {
          payload.city = formData.city;
        }
        if (!original || formData.state !== original.state) {
          payload.state = formData.state;
        }
        if (!original || formData.zip !== original.zip) {
          payload.zipCode = formData.zip;
        }
        if (
          !original ||
          JSON.stringify(formData.privileges) !==
            JSON.stringify(original.privileges)
        ) {
          payload.privileges = formData.privileges;
        }

        const result = await editTechnicianApiService(
          technicianId,
          payload,
          newProfile,
          newDocs
        );

        if (!result.success) {
          setSnackbarError(
            result.forbidden
              ? TECHNICIAN_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP
              : TECHNICIAN_ERROR_MESSAGES.UNEXPECTED_ERROR
          );
          setIsSnackbarOpen(true);
          return;
        }

        navigate('/technicians', {
          replace: true,
          state: { showEditTechnicianSuccess: true },
        });
      } catch (_error) {
        setSnackbarError(TECHNICIAN_ERROR_MESSAGES.UNEXPECTED_ERROR);
        setIsSnackbarOpen(true);
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, technicianId, navigate, deletedDocPaths, profileFile, docFiles]
  );

  const hasChanges = useMemo(() => {
    const original = originalRef.current;
    if (!original) {
      return false;
    }
    const fieldsChanged =
      formData.name !== original.formData.name ||
      formData.phone !== original.formData.phone ||
      formData.countryCode !== original.formData.countryCode ||
      formData.address1 !== original.formData.address1 ||
      formData.address2 !== original.formData.address2 ||
      formData.city !== original.formData.city ||
      formData.state !== original.formData.state ||
      formData.zip !== original.formData.zip ||
      JSON.stringify(formData.privileges) !==
        JSON.stringify(original.formData.privileges);

    if (fieldsChanged) {
      return true;
    }
    if (profileFile !== undefined) {
      return true;
    }
    if (docFiles.length > 0) {
      return true;
    }
    if (deletedDocPaths.length > 0) {
      return true;
    }
    return false;
  }, [formData, profileFile, docFiles, deletedDocPaths]);

  const handleCancel = useCallback(() => {
    if (!hasChanges) {
      navigate('/technicians', { replace: true });
      return;
    }
    setIsCancelModalOpen(true);
  }, [hasChanges, navigate]);

  const handleCloseCancelModal = useCallback(() => {
    setIsCancelModalOpen(false);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    setIsCancelModalOpen(false);
    navigate('/technicians', { replace: true });
  }, [navigate]);

  const handleCloseSnackbar = useCallback(() => {
    setIsSnackbarOpen(false);
    setSnackbarError('');
  }, []);

  const handleStatusChange = useCallback(() => {
    setIsStatusDialogOpen(true);
  }, []);

  const handleCloseStatusDialog = useCallback(() => {
    setIsStatusDialogOpen(false);
  }, []);

  const handleConfirmStatusUpdate = useCallback(async () => {
    if (!technicianId) {
      return;
    }

    setIsStatusSubmitting(true);
    try {
      const newStatus = status === 'active' ? false : true;
      const result =
        await updateTechnicianStatusApiService.updateTechnicianStatus({
          technicianId,
          isActive: newStatus,
        });

      if (result.success) {
        setStatus(newStatus ? 'active' : 'inactive');
        setStatusSuccessMessage(
          newStatus
            ? 'Technik został pomyślnie aktywowany.'
            : 'Technik został pomyślnie dezaktywowany.'
        );
        setShowStatusSuccessSnackbar(true);
        setIsStatusDialogOpen(false);
      } else {
        setStatusErrorMessage(
          result.error || 'Wystąpił błąd podczas aktualizacji statusu.'
        );
        setShowStatusErrorSnackbar(true);
        setIsStatusDialogOpen(false);
      }
    } catch (_error) {
      setStatusErrorMessage('Wystąpił błąd podczas aktualizacji statusu.');
      setShowStatusErrorSnackbar(true);
      setIsStatusDialogOpen(false);
    } finally {
      setIsStatusSubmitting(false);
    }
  }, [technicianId, status]);

  const handleCloseStatusSuccessSnackbar = useCallback(() => {
    setShowStatusSuccessSnackbar(false);
    setStatusSuccessMessage('');
  }, []);

  const handleCloseStatusErrorSnackbar = useCallback(() => {
    setShowStatusErrorSnackbar(false);
    setStatusErrorMessage('');
  }, []);

  return {
    formData,
    setFormData,
    errors,
    submitErrorTrigger,
    isSubmitting,
    isLoading,
    accessError,
    snackbarError,
    isSnackbarOpen,
    handleCloseSnackbar,
    isCancelModalOpen,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleNameChange,
    handlePhoneChange,
    handleCountryCodeChange,
    handleAddress1Change,
    handleAddress2Change,
    handleCityChange,
    handleStateChange,
    handleZipChange,
    handleProfileFileChange,
    handleProfileFileDelete,
    handleProfileFileError,
    handleDocFilesChange,
    handleExistingDocDelete,
    handleDocFilesError,
    handlePrivilegesChange,
    handleSubmit,
    handleCancel,
    profileFile,
    existingProfileUrl,
    docFiles,
    existingDocs,
    status,
    handleStatusChange,
    isStatusDialogOpen,
    handleCloseStatusDialog,
    handleConfirmStatusUpdate,
    isStatusSubmitting,
    showStatusSuccessSnackbar,
    statusSuccessMessage,
    handleCloseStatusSuccessSnackbar,
    showStatusErrorSnackbar,
    statusErrorMessage,
    handleCloseStatusErrorSnackbar,
  };
};
